/**
 * Classifies history diffs as text-only edits that can be applied via
 * surgical iframe sync, avoiding a full iframe re-render.
 */

import { injectEditableAttrs } from './editable-utils.js';

const SECTION_HTML_PATCH_RE = /^pages\.(\d+)\.sections\.(\d+)\.html$/;
const SHARED_HTML_PATCH_RE = /^sharedSections\.(navigation|footer)\.html$/;
const INLINE_COLOR_CLASSES = new Set([
  'pb-inline-color-accent',
  'pb-inline-color-heading',
  'pb-inline-color-body'
]);

function getByPath(obj, path) {
  if (!obj || typeof path !== 'string') return undefined;
  const keys = path.split('.');
  let current = obj;
  for (const key of keys) {
    if (current == null) return undefined;
    current = current[key];
  }
  return current;
}

function parseNormalizedRoot(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return null;
  injectEditableAttrs(root);
  return root;
}

function hasBindingMarker(el) {
  return !!(el && (el.hasAttribute('data-pb-client') || el.hasAttribute('data-pb-client-text')));
}

function tokenizeClassList(value) {
  if (!value) return new Set();
  return new Set(
    String(value)
      .split(/\s+/)
      .map((token) => token.trim())
      .filter(Boolean)
  );
}

function isAllowedTextEditableClassDiff(oldValue, newValue) {
  const before = tokenizeClassList(oldValue);
  const after = tokenizeClassList(newValue);

  const removed = [];
  const added = [];

  for (const token of before) {
    if (!after.has(token)) removed.push(token);
  }
  for (const token of after) {
    if (!before.has(token)) added.push(token);
  }

  for (const token of removed) {
    if (!INLINE_COLOR_CLASSES.has(token)) return false;
  }
  for (const token of added) {
    if (!INLINE_COLOR_CLASSES.has(token)) return false;
  }
  return true;
}

function compareAttributes(oldEl, newEl, { inTextEditable, inBinding }) {
  if (inBinding) return true;

  const attrNames = new Set();
  for (const attr of Array.from(oldEl.attributes)) attrNames.add(attr.name);
  for (const attr of Array.from(newEl.attributes)) attrNames.add(attr.name);

  for (const name of attrNames) {
    const oldValue = oldEl.getAttribute(name);
    const newValue = newEl.getAttribute(name);
    if (oldValue === newValue) continue;

    if (inTextEditable && name === 'class' && isAllowedTextEditableClassDiff(oldValue, newValue)) {
      continue;
    }

    if (
      (name === 'href' || name === 'target' || name === 'rel') &&
      oldEl.tagName === 'A' &&
      newEl.tagName === 'A'
    ) {
      continue;
    }

    return false;
  }

  return true;
}

function compareNode(oldNode, newNode, context) {
  if (!oldNode || !newNode) return false;
  if (oldNode.nodeType !== newNode.nodeType) return false;

  if (oldNode.nodeType === Node.TEXT_NODE) {
    if (context.inTextEditable) return true;
    return oldNode.textContent === newNode.textContent;
  }

  if (oldNode.nodeType !== Node.ELEMENT_NODE) {
    return true;
  }

  if (oldNode.tagName !== newNode.tagName) return false;

  const isTextEditableRoot =
    oldNode.getAttribute('data-pb-editable') === 'text' &&
    newNode.getAttribute('data-pb-editable') === 'text';
  const inTextEditable = context.inTextEditable || isTextEditableRoot;
  const inBinding = context.inBinding || hasBindingMarker(oldNode) || hasBindingMarker(newNode);

  if (!compareAttributes(oldNode, newNode, { inTextEditable, inBinding })) {
    return false;
  }

  // Inside a text editable root, descendant structure/content can vary.
  if (inTextEditable && !context.inTextEditable) {
    return true;
  }

  if (oldNode.childNodes.length !== newNode.childNodes.length) return false;

  for (let i = 0; i < oldNode.childNodes.length; i++) {
    if (!compareNode(oldNode.childNodes[i], newNode.childNodes[i], { inTextEditable, inBinding })) {
      return false;
    }
  }

  return true;
}

function compareHtmlStructureAllowingTextEdits(oldHtml, newHtml) {
  const oldRoot = parseNormalizedRoot(oldHtml);
  const newRoot = parseNormalizedRoot(newHtml);
  if (!oldRoot || !newRoot) return false;
  return compareNode(oldRoot, newRoot, { inTextEditable: false, inBinding: false });
}

function isSectionHtmlPatchPath(path) {
  return SECTION_HTML_PATCH_RE.test(path) || SHARED_HTML_PATCH_RE.test(path);
}

export function isTextOnlyDiff(patches, oldState = null, newState = null) {
  if (!Array.isArray(patches) || patches.length === 0) return false;

  for (const patch of patches) {
    if (!patch || patch.op !== 'replace' || typeof patch.path !== 'string') return false;
    if (!isSectionHtmlPatchPath(patch.path)) return false;

    const oldHtml =
      typeof patch.oldValue === 'string' ? patch.oldValue : getByPath(oldState, patch.path);
    const newHtml =
      typeof patch.newValue === 'string' ? patch.newValue : getByPath(newState, patch.path);
    if (typeof oldHtml !== 'string' || typeof newHtml !== 'string') return false;

    if (!compareHtmlStructureAllowingTextEdits(oldHtml, newHtml)) {
      return false;
    }
  }

  return true;
}
